import React, { useState, useEffect } from "react";
import { Card, CardBody, Container, Row, Col, Badge, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import CampaignSchedule from "../../services/OrderingServices/CampaignSchedule";
import "./CampaignSchedule.css";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import EventDetailModal from "./CampaingModal";

const CampaignCalendar = () => {
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [campaignDetailsMap, setCampaignDetailsMap] = useState(new Map());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCampaigns: 0,
    activeCampaigns: 0,
    upcomingCampaigns: 0
  });

  const fetchCalendarEvents = async () => {
    try {
      setLoading(true);
      const schedules = await CampaignSchedule.campaignCalendar();

      const events = [];
      const campaignDetailsMap = new Map();
      const uniqueCampaigns = new Set();
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      schedules.forEach((day) => {
        const date = day.date.split("T")[0];
        const dayDate = new Date(date);
        
        const campaignsPerDay = new Map();

        day.time.forEach((slot) => {
          slot.campaigns.forEach((campaign) => {
            const campaignKey = `${campaign.id}_${date}`;
            uniqueCampaigns.add(campaign.id);
            
            if (!campaignsPerDay.has(campaignKey)) {
              campaignsPerDay.set(campaignKey, {
                id: campaign.id,
                name: campaign.name,
                date: date,
                timeSlots: []
              });
            }
            campaignsPerDay.get(campaignKey).timeSlots.push(slot.time);
          });
        });

        campaignsPerDay.forEach((campaignData, campaignKey) => {
          const sortedTimeSlots = [...campaignData.timeSlots].sort();
          const firstTimeSlot = sortedTimeSlots[0];
          
          campaignDetailsMap.set(campaignKey, campaignData);

          const startDateTime = `${date}T${firstTimeSlot}:00`;
          const eventDate = new Date(startDateTime);
          
          // Assign colors based on campaign ID for consistency
          const colors = [
            '#4f46e5', // Indigo
            '#0891b2', // Cyan
            '#7c3aed', // Violet
            '#db2777', // Pink
            '#ea580c', // Orange
            '#059669', // Emerald
          ];
          const colorIndex = campaignData.id % colors.length;
          
          events.push({
            id: campaignKey,
            title: campaignData.name,
            start: startDateTime,
            backgroundColor: colors[colorIndex],
            borderColor: colors[colorIndex],
            extendedProps: {
              campaignId: campaignData.id,
              campaignKey: campaignKey,
              timeSlotCount: campaignData.timeSlots.length,
              status: eventDate < today ? 'completed' : eventDate.toDateString() === today.toDateString() ? 'active' : 'upcoming'
            }
          });
        });
      });

      // Calculate stats
      const now = new Date();
      const activeCampaigns = events.filter(e => {
        const eventDate = new Date(e.start);
        return eventDate.toDateString() === now.toDateString();
      }).length;

      const upcomingCampaigns = events.filter(e => {
        const eventDate = new Date(e.start);
        return eventDate > now;
      }).length;

      setStats({
        totalCampaigns: uniqueCampaigns.size,
        activeCampaigns,
        upcomingCampaigns
      });

      setLoading(false);
      return { events, campaignDetailsMap };
    } catch (error) {
      console.error("Error fetching campaign schedules:", error);
      toast.error("Failed to load campaign schedules");
      setLoading(false);
      return { events: [], campaignDetailsMap: new Map() };
    }
  };

  const handleEventClick = (clickInfo) => {
    const campaignKey = clickInfo.event.extendedProps.campaignKey;
    const campaignDetails = campaignDetailsMap.get(campaignKey);
    
    setSelectedEvent({
      title: clickInfo.event.title,
      date: clickInfo.event.start,
      campaignId: clickInfo.event.extendedProps.campaignId,
      timeSlots: campaignDetails?.timeSlots || [],
      allDetails: campaignDetails
    });
    setModalOpen(true);
  };

  useEffect(() => {
    const loadEvents = async () => {
      const { events, campaignDetailsMap } = await fetchCalendarEvents();
      setCalendarEvents(events);
      setCampaignDetailsMap(campaignDetailsMap);
    };
    loadEvents();
  }, []);

  const renderEventContent = (eventInfo) => {
    const slotCount = eventInfo.event.extendedProps.timeSlotCount;
    return (
      <div className="fc-event-content-wrapper">
        <div className="fc-event-main-frame">
          <div className="fc-event-time-wrapper">
            <span className="fc-event-time">{eventInfo.timeText}</span>
          </div>
          <div className="fc-event-title-wrapper">
            <span className="fc-event-title">{eventInfo.event.title}</span>
            {slotCount > 1 && (
              <span className="fc-event-badge">{slotCount} slots</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Campaign Calendar" pageTitle="Campaigns" />
        
        {/* Stats Cards */}
        {/* <Row className="mb-4">
          <Col xl={4} md={6}>
            <Card className="card-animate shadow-sm border-0">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <p className="text-uppercase fw-medium text-muted mb-2">
                      Total Campaigns
                    </p>
                    <h4 className="fs-22 fw-semibold mb-0">
                      {stats.totalCampaigns}
                    </h4>
                  </div>
                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-soft-primary text-primary rounded-circle fs-3">
                      <i className="ri-calendar-event-line"></i>
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl={4} md={6}>
            <Card className="card-animate shadow-sm border-0">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <p className="text-uppercase fw-medium text-muted mb-2">
                      Active Today
                    </p>
                    <h4 className="fs-22 fw-semibold mb-0">
                      {stats.activeCampaigns}
                    </h4>
                  </div>
                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-soft-success text-success rounded-circle fs-3">
                      <i className="ri-play-circle-line"></i>
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl={4} md={6}>
            <Card className="card-animate shadow-sm border-0">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <p className="text-uppercase fw-medium text-muted mb-2">
                      Upcoming
                    </p>
                    <h4 className="fs-22 fw-semibold mb-0">
                      {stats.upcomingCampaigns}
                    </h4>
                  </div>
                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-soft-info text-info rounded-circle fs-3">
                      <i className="ri-time-line"></i>
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row> */}

        {/* Calendar Card */}
        <Card className="shadow-sm border-0">
          <CardBody className="p-4">
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="text-muted mt-3">Loading campaigns...</p>
              </div>
            ) : (
              <div className="campaign-calendar-wrapper">
                <FullCalendar
                  plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                  initialView="dayGridMonth"
                  weekends={true}
                  events={calendarEvents}
                  eventContent={renderEventContent}
                  eventClick={handleEventClick}
                  headerToolbar={{
                    left: "prev,next today",
                    center: "title",
                    right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
                  }}
                  height="auto"
                  eventDisplay="block"
                  displayEventTime={true}
                  eventTimeFormat={{
                    hour: '2-digit',
                    minute: '2-digit',
                    meridiem: 'short'
                  }}
                  dayMaxEvents={3}
                  moreLinkClick="popover"
                  nowIndicator={true}
                  navLinks={true}
                  businessHours={{
                    daysOfWeek: [1, 2, 3, 4, 5],
                    startTime: '09:00',
                    endTime: '18:00',
                  }}
                />
              </div>
            )}
          </CardBody>
        </Card>

        {/* Event Detail Modal */}
        <EventDetailModal
          isOpen={modalOpen}
          toggle={() => setModalOpen(!modalOpen)}
          event={selectedEvent}
        />
      </Container>
    </div>
  );
};

export default CampaignCalendar;